import { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { type OnboardingStep, ONBOARDING_STEP_ALIASES, ONBOARDING_STEPS } from './steps/steps';
import { useVisionOnboardingCompleteMutation, VisionOnboardingCompleteMutation } from './graphql/visionOnboardingComplete';
import { useToast } from '../../../utils/contexts/toast-provider';
import { GetVisionStatusDocument } from '../../../utils/hooks/getVisionStatus';
import { VisionConsentModal } from './VisionConsentModal';
interface OnboardingContextType {
  step: OnboardingStep;
  next: () => void;
  back: () => void;
  isCompleting: boolean;
}
const OnboardingContext = createContext<OnboardingContextType | null>(null);
const isStep = (value: string, steps: readonly OnboardingStep[]): value is OnboardingStep => {
  return steps.includes(value as OnboardingStep);
};
const getStepAlias = (value: string): OnboardingStep | undefined => {
  return ONBOARDING_STEP_ALIASES[value as keyof typeof ONBOARDING_STEP_ALIASES];
};
export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboardingContext must be used within an OnboardingProvider');
  }
  return context;
};
export const OnboardingProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const router = useRouter();
  const {
    addToast
  } = useToast();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [isNavigatingToGallery, setIsNavigatingToGallery] = useState(false);
  const steps = ONBOARDING_STEPS;
  const step = useMemo(() => {
    const stepFromQuery = router.query.step as string;
    const defaultStep = 'welcome';
    const normalizedStep = stepFromQuery ? getStepAlias(stepFromQuery) ?? stepFromQuery : defaultStep;
    return isStep(normalizedStep, steps) ? normalizedStep : defaultStep;
  }, [router.query.step, steps]);
  const [completeOnboardingMutation, {
    loading: isCompleting
  }] = useVisionOnboardingCompleteMutation({
    onCompleted: (data: VisionOnboardingCompleteMutation) => {
      const result = data?.visionOnboardingComplete?.result;
      if (result?.__typename === 'VisionOnboardingCompleteSuccess') {
        setIsNavigatingToGallery(true);
        router.push('/gallery');
      } else if (result?.__typename === 'VisionOnboardingCompleteError') {
        setShowConsentModal(false);
        addToast({
          message: 'Sorry, something went wrong. Please try again.',
          status: 'error'
        });
      }
    },
    onError: () => {
      setShowConsentModal(false);
      addToast({
        message: 'Sorry, something went wrong. Please try again.',
        status: 'error'
      });
    }
  });
  const handleCompleteOnboarding = useCallback(async () => {
    await completeOnboardingMutation({
      refetchQueries: [{
        query: GetVisionStatusDocument
      }],
      awaitRefetchQueries: true
    });
  }, [completeOnboardingMutation]);
  const goto = useCallback((targetStep: OnboardingStep) => {
    router.push(`/gallery/onboarding/${targetStep}`, '', {
      shallow: true
    });
  }, [router]);
  const next = useCallback(() => {
    const currentIndex = steps.findIndex(s => s === step);
    const nextStep = steps[currentIndex + 1];
    if (nextStep) {
      goto(nextStep);
    } else {
      setShowConsentModal(true);
    }
  }, [step, goto, steps]);
  const back = useCallback(() => {
    const currentIndex = steps.findIndex(s => s === step);
    const prevStep = steps[currentIndex - 1];
    if (prevStep) {
      goto(prevStep);
    } else {
      router.push('/home');
    }
  }, [step, goto, steps, router]);
  const value = useMemo(() => ({
    step,
    next,
    back,
    isCompleting
  }), [step, next, back, isCompleting]);
  const handleConsentConfirm = useCallback(() => {
    handleCompleteOnboarding();
  }, [handleCompleteOnboarding]);
  const handleConsentCancel = useCallback(() => {
    setShowConsentModal(false);
  }, []);
  return <OnboardingContext.Provider value={value}>
      {children}
      <VisionConsentModal isOpen={showConsentModal} onConfirm={handleConsentConfirm} onCancel={handleConsentCancel} isConfirming={isCompleting || isNavigatingToGallery} />
    </OnboardingContext.Provider>;
};